import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  askBiReport,
  biReportsPageQuery,
  deleteBiCustomReport,
  downloadBiPdf,
  downloadBiXlsx,
  previewBiReport,
  runBiReport,
  saveBiCustomReport,
  suggestBi,
  type BiAnswer,
  type BiCatalogEntry,
  type BiColumnSpec,
  type BiDataset,
  type BiField,
  type BiFilter,
  type BiFilterValues,
  type BiOption,
  type BiPage,
  type BiResult,
  type BiRow,
} from '../../api/settingsBiReports'
import { Forbidden } from '../../ui/Forbidden'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { CompanyReportsFrame } from './CompanyReportsPage'
import './BiReportsPage.css'

/**
 * Settings -> Reports -> BI Reports (ITIL) - `settings/company_reports/bi_report`,
 * macheta `views/version-3/settings/company_reports/bi/index.php` (cu scriptul
 * ei in linie), element cu element:
 *
 *  - banda de acoperire ITIL (cerinta 39);
 *  - casuta "Ask for a report in your own words", cu exemplele ei;
 *  - registrul (predefinite + rapoartele salvate), filtrele lui, Apply,
 *    Export XLSX, Export PDF, Edit in builder, Delete report;
 *  - constructorul no-code: sursa, paleta, Coloane si Filtre prin drag & drop
 *    (sau clic), socoteala si felia de timp pe chip, sortarea, gruparea,
 *    rolurile, numele, Preview, Save.
 *
 * STAREA E ADEVARUL, DOM-ul doar o deseneaza - ca in scriptul vechi: fiecare
 * drag & drop schimba listele din `builder`, si zonele se redeseneaza din ele.
 *
 * CE E ALTFEL, si de ce
 * ---------------------
 * - Casutele care isi cautau valorile prin select2 (`tags: true`, ajax) sunt
 *   o casuta de text cu o lista de sugestii (`<datalist>`) umpluta de pe
 *   server: filtrul ramane un "contine", textul liber merge mai departe, si
 *   sugestiile vin cu aceleasi margini ca raportul.
 * - Listele cu selectie multipla sunt `SelectPicker`, ca peste tot in v4.
 * - Tabelele nu mai sunt DataTables: au aceeasi cautare si aceeasi paginare
 *   de 25, facute in pagina (`PagedTable`).
 * - Exporturile vin direct in raspuns si se salveaza din pagina; nu mai
 *   exista `download_script_file`.
 */

const NO_DATA_TAIL = ' — no data on this instance'

const PAGE_LENGTH = 25

const ASK_EXAMPLES = [
  'which windows endpoints reported in the last 7 days',
  'how many tickets by type this month',
  'which machines are missing patches',
  'free space on disks by computer',
  'who signed in on computers in the last 7 days',
]

function isoDate(daysAgo: number): string {
  const d = new Date()
  d.setDate(d.getDate() - daysAgo)
  return d.toISOString().slice(0, 10)
}

function withDataMark(label: string, def: { has_data?: boolean }): string {
  return def.has_data === false ? label + NO_DATA_TAIL : label
}

function failMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback
}

function typeIcon(type: string): string {
  if (type === 'date') return 'fa-calendar-alt'
  if (type === 'number') return 'fa-hashtag'
  return type === 'select' ? 'fa-list' : 'fa-font'
}

/** Cheia coloanei, in oglinda cu `BiCustomReports::columnKey()`. */
function columnKey(spec: BiColumnSpec): string {
  return spec.field + (spec.bucket ? '__' + spec.bucket : '') + (spec.agg ? '__' + spec.agg : '')
}

function cell(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value)
}

/* -------------------------------------------------------------------------
 * Tabelul cu cautare si paginare, ca DataTables
 * ---------------------------------------------------------------------- */

function PagedTable({ id, columns, rows }: { id: string; columns: Record<string, string>; rows: BiRow[] }) {
  const [needle, setNeedle] = useState('')
  const [page, setPage] = useState(0)
  const fields = Object.keys(columns)

  const shown = useMemo(() => {
    const q = needle.trim().toLowerCase()
    if (q === '') return rows
    return rows.filter((row) => fields.some((f) => cell(row[f]).toLowerCase().includes(q)))
  }, [rows, needle, fields])

  const pages = Math.max(1, Math.ceil(shown.length / PAGE_LENGTH))
  const current = Math.min(page, pages - 1)
  const slice = shown.slice(current * PAGE_LENGTH, current * PAGE_LENGTH + PAGE_LENGTH)

  return (
    <div>
      <div className="bi-table-bar">
        <label>
          <i className="fas fa-search" aria-hidden="true"></i>{' '}
          <input
            type="search"
            className="form-control input-sm"
            placeholder="Search..."
            aria-label="Search"
            value={needle}
            onChange={(e) => {
              setNeedle(e.target.value)
              setPage(0)
            }}
          />
        </label>
        <span className="text-muted">
          Showing {shown.length === 0 ? 0 : current * PAGE_LENGTH + 1} to {Math.min(shown.length, (current + 1) * PAGE_LENGTH)} of {shown.length} entries
        </span>
      </div>
      <table id={id} className="table table-hover table-bordered" style={{ width: '100%' }}>
        <thead>
          <tr>
            {fields.map((f) => (
              <th key={f}>{columns[f]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slice.length === 0 ? (
            <tr>
              <td colSpan={Math.max(1, fields.length)} className="text-muted">
                No data available in table
              </td>
            </tr>
          ) : (
            slice.map((row, i) => (
              <tr key={current * PAGE_LENGTH + i}>
                {fields.map((f) => (
                  <td key={f}>{cell(row[f])}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {pages > 1 ? (
        <div className="bi-table-bar">
          <span></span>
          <span>
            <button type="button" className="btn btn-default btn-xs" disabled={current === 0} onClick={() => setPage(current - 1)}>
              Previous
            </button>{' '}
            <span className="text-muted">
              page {current + 1} of {pages}
            </span>{' '}
            <button type="button" className="btn btn-default btn-xs" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
              Next
            </button>
          </span>
        </div>
      ) : null}
    </div>
  )
}

/** Cand raportul iese gol, motivul - nu tacerea. Vine gata scris de la server. */
function EmptyNote({ result }: { result: BiResult | null }) {
  if (!result || !result.empty_note || result.rows.length > 0) return null
  return (
    <p className="bi-empty-note">
      <i className="fas fa-info-circle" aria-hidden="true"></i> {result.empty_note}
    </p>
  )
}

/* -------------------------------------------------------------------------
 * Casuta cu sugestii de pe server (fostul select2 cu `tags: true`)
 * ---------------------------------------------------------------------- */

function SuggestInput({ id, source, value, onChange, className = 'form-control' }: {
  id: string
  source: string
  value: string
  onChange(value: string): void
  className?: string
}) {
  const [options, setOptions] = useState<string[]>([])
  const timer = useRef<number | null>(null)

  const lookup = (needle: string) => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    if (needle.trim().length < 2) {
      setOptions([])
      return
    }
    timer.current = window.setTimeout(() => {
      suggestBi(source, needle)
        .then((rows) => setOptions(rows.map((r) => r.text)))
        .catch(() => setOptions([]))
    }, 250)
  }

  return (
    <>
      <input
        id={id}
        type="text"
        className={className}
        list={id + '-list'}
        placeholder="type to search…"
        autoComplete="off"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          lookup(e.target.value)
        }}
      />
      <datalist id={id + '-list'}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  )
}

/* -------------------------------------------------------------------------
 * Filtrele unui raport din registru
 * ---------------------------------------------------------------------- */

function initialFilterValues(filters: BiFilter[]): BiFilterValues {
  const values: BiFilterValues = {}
  for (const filter of filters) {
    if (filter.type === 'date') {
      const isFrom = filter.name === 'from' || /_from$/.test(filter.name)
      values[filter.name] = isFrom ? isoDate(30) : isoDate(0)
    } else if (filter.type === 'text' && filter.operators) {
      values[filter.name + '_op'] = 'contains'
    }
  }
  return values
}

/** O perioada aleasa stinge cele doua date de sub ea, ca sa nu para ca raportul le asculta. */
function periodChosen(values: BiFilterValues, name: string): boolean {
  const base = name.replace(/_(from|to)$/, '')
  return base !== name && typeof values[base + '_period'] === 'string' && values[base + '_period'] !== ''
}

function ReportFilters({ filters, values, onChange }: {
  filters: BiFilter[]
  values: BiFilterValues
  onChange(values: BiFilterValues): void
}) {
  const set = (name: string, value: string | string[]) => onChange({ ...values, [name]: value })

  return (
    <div id="bi-filters" className="row">
      {filters.map((filter) => {
        const id = 'bi-filter-' + filter.name
        let input: ReactNode
        const text = typeof values[filter.name] === 'string' ? (values[filter.name] as string) : ''
        const list = Array.isArray(values[filter.name]) ? (values[filter.name] as string[]) : []

        if (filter.type === 'date') {
          const disabled = periodChosen(values, filter.name)
          input = (
            <input
              id={id}
              type="date"
              className="form-control bi-filter"
              value={text}
              disabled={disabled}
              style={{ opacity: disabled ? 0.5 : 1 }}
              onChange={(e) => set(filter.name, e.target.value)}
            />
          )
        } else if (filter.type === 'period') {
          input = (
            <select id={id} className="form-control bi-filter bi-period" value={text} onChange={(e) => set(filter.name, e.target.value)}>
              {(filter.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )
        } else if (filter.type === 'number') {
          input = <input id={id} type="number" step="any" className="form-control bi-filter" value={text} onChange={(e) => set(filter.name, e.target.value)} />
        } else if (filter.type === 'select') {
          input = (
            <select id={id} className="form-control bi-filter" value={text} onChange={(e) => set(filter.name, e.target.value)}>
              <option value="">All</option>
              {(filter.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )
        } else if (filter.type === 'multiselect') {
          input = (
            <SelectPicker
              id={id}
              className="form-control bi-filter bi-choice"
              title="any"
              multiple
              value={list}
              options={filter.options ?? []}
              onChange={(picked) => set(filter.name, picked)}
            />
          )
        } else {
          input = (
            <>
              {filter.operators ? (
                <select
                  className="form-control bi-filter bi-filter-op"
                  aria-label={filter.label + ' operator'}
                  value={typeof values[filter.name + '_op'] === 'string' ? (values[filter.name + '_op'] as string) : 'contains'}
                  onChange={(e) => set(filter.name + '_op', e.target.value)}
                >
                  {filter.operators.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {filter.suggest ? (
                <SuggestInput id={id} source={filter.suggest} value={text} onChange={(v) => set(filter.name, v)} className="form-control bi-filter bi-suggest" />
              ) : (
                <input id={id} type="text" className="form-control bi-filter" value={text} onChange={(e) => set(filter.name, e.target.value)} />
              )}
            </>
          )
        }

        return (
          <div className="col-md-4" key={filter.name}>
            <div className="form-group">
              <label className="control-label" htmlFor={id}>
                {filter.label}:
              </label>
              {input}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Valorile care chiar pleaca la server: fara cele goale si fara datele stinse de o perioada. */
function collectFilters(values: BiFilterValues): BiFilterValues {
  const out: BiFilterValues = {}
  for (const [name, value] of Object.entries(values)) {
    if (periodChosen(values, name)) continue
    if (Array.isArray(value) ? value.length > 0 : value !== '') out[name] = value
  }
  return out
}

/**
 * Antetul PDF-ului spune si cu ce filtre s-a scos raportul. Etichetele
 * optiunilor traiesc doar aici, in ecran - serverul primeste valori.
 */
function filterSummary(filters: BiFilter[], values: BiFilterValues): string {
  const pieces: string[] = []
  const labelOf = (options: BiOption[] | undefined, value: string) => options?.find((o) => o.value === value)?.label ?? value
  for (const filter of filters) {
    const value = values[filter.name]
    if (periodChosen(values, filter.name) || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) continue
    const shown = Array.isArray(value) ? value.map((v) => labelOf(filter.options, v)).join(', ') : labelOf(filter.options, value)
    pieces.push(filter.label + ': ' + shown)
    if (filter.type === 'text' && filter.operators) {
      const op = values[filter.name + '_op']
      if (typeof op === 'string' && op !== '') pieces.push(filter.label + ' operator: ' + labelOf(filter.operators, op))
    }
  }
  return pieces.join(' · ')
}

/* -------------------------------------------------------------------------
 * Constructorul no-code
 * ---------------------------------------------------------------------- */

type Builder = {
  dataset: string
  columns: BiColumnSpec[]
  filters: string[]
  values: BiFilterValues
  sortBy: string
  sortDir: 'asc' | 'desc'
  group: boolean
  roles: string[]
  name: string
  editing: number
  editingName: string
}

function emptyBuilder(dataset: string, datasets: Record<string, BiDataset>): Builder {
  const filters: string[] = []
  const values: BiFilterValues = {}
  if (dataset && datasets[dataset]) {
    /* Perioadele obligatorii intra singure la filtre - fara ele raportul oricum n-ar rula. */
    for (const field of datasets[dataset].fields) {
      if (field.required) {
        filters.push(field.name)
        values[field.name + '_from'] = isoDate(30)
        values[field.name + '_to'] = isoDate(0)
      }
    }
  }
  return { dataset, columns: [], filters, values, sortBy: '', sortDir: 'asc', group: false, roles: [], name: '', editing: 0, editingName: '' }
}

function fieldMeta(datasets: Record<string, BiDataset>, dataset: string, name: string): BiField | null {
  return datasets[dataset]?.fields.find((f) => f.name === name) ?? null
}

function columnLabel(field: BiField, spec: BiColumnSpec): string {
  const label = field.label + (spec.bucket ? ' (' + spec.bucket + ')' : '')
  const template = field.aggregates.find((a) => a.value === spec.agg)?.template ?? ''
  return spec.agg ? template.replace('%s', label) : label
}

function builderDefinition(b: Builder) {
  return {
    columns: b.columns,
    filters: b.filters,
    sort: { by: b.sortBy, dir: b.sortDir },
    group: b.group ? 1 : 0,
  }
}

function FilterChipInputs({ field, values, periods, operators, onChange }: {
  field: BiField
  values: BiFilterValues
  periods: BiOption[]
  operators: BiOption[]
  onChange(name: string, value: string | string[]): void
}) {
  const name = field.name
  const text = (key: string) => (typeof values[key] === 'string' ? (values[key] as string) : '')

  if (field.type === 'date') {
    const chosen = text(name + '_period') !== ''
    return (
      <>
        {' '}
        <select className="form-control bb-value bb-period" aria-label={field.label + ' period'} value={text(name + '_period')} onChange={(e) => onChange(name + '_period', e.target.value)}>
          <option value="">between the dates</option>
          {periods.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>{' '}
        <input type="date" className="form-control bb-value" aria-label={field.label + ' from'} value={text(name + '_from')} disabled={chosen} style={{ opacity: chosen ? 0.5 : 1 }} onChange={(e) => onChange(name + '_from', e.target.value)} />
        {' — '}
        <input type="date" className="form-control bb-value" aria-label={field.label + ' to'} value={text(name + '_to')} disabled={chosen} style={{ opacity: chosen ? 0.5 : 1 }} onChange={(e) => onChange(name + '_to', e.target.value)} />
      </>
    )
  }

  if (field.type === 'number') {
    return (
      <>
        {' '}
        <input type="number" step="any" className="form-control bb-value" placeholder="at least" aria-label={field.label + ' at least'} value={text(name + '_min')} onChange={(e) => onChange(name + '_min', e.target.value)} />{' '}
        <input type="number" step="any" className="form-control bb-value" placeholder="at most" aria-label={field.label + ' at most'} value={text(name + '_max')} onChange={(e) => onChange(name + '_max', e.target.value)} />
      </>
    )
  }

  if (field.type === 'select') {
    return (
      <>
        {' '}
        <SelectPicker
          id={'bb-value-' + name}
          className="form-control bb-value bb-choice"
          title="any"
          multiple
          value={Array.isArray(values[name]) ? (values[name] as string[]) : []}
          options={field.options}
          onChange={(picked) => onChange(name, picked)}
        />
      </>
    )
  }

  return (
    <>
      {' '}
      <select className="form-control bb-value" aria-label={field.label + ' operator'} value={text(name + '_op') || 'contains'} onChange={(e) => onChange(name + '_op', e.target.value)}>
        {operators.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>{' '}
      {field.suggest ? (
        <SuggestInput id={'bb-value-' + name} source={field.suggest} value={text(name)} onChange={(v) => onChange(name, v)} className="form-control bb-value bb-suggest" />
      ) : (
        <input type="text" className="form-control bb-value" placeholder="value..." aria-label={field.label} value={text(name)} onChange={(e) => onChange(name, e.target.value)} />
      )}
    </>
  )
}

function ReportBuilder({ page, builder, setBuilder, onSaved }: {
  page: BiPage
  builder: Builder
  setBuilder(next: Builder): void
  onSaved(key: string, entry: BiCatalogEntry): void
}) {
  const datasets = page.builder
  const [over, setOver] = useState<'columns' | 'filters' | null>(null)
  const [preview, setPreview] = useState<BiResult | null>(null)
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null)
  const [needsName, setNeedsName] = useState(false)
  const nameBox = useRef<HTMLInputElement>(null)

  const ready = builder.dataset !== '' && builder.columns.length > 0
  const dataset = builder.dataset ? datasets[builder.dataset] : null

  const reset = (ds: string) => {
    setBuilder(emptyBuilder(ds, datasets))
    setPreview(null)
    setNeedsName(false)
  }

  const setColumns = (columns: BiColumnSpec[]) => {
    const keys = columns.map(columnKey)
    const sortBy = keys.includes(builder.sortBy) ? builder.sortBy : (keys[0] ?? '')
    setBuilder({ ...builder, columns, sortBy })
  }

  const addColumn = (name: string, beforeIndex: number | null) => {
    const spec: BiColumnSpec = { field: name, agg: '', bucket: '' }
    const columns = builder.columns.slice()
    if (beforeIndex === null || beforeIndex < 0 || beforeIndex >= columns.length) columns.push(spec)
    else columns.splice(beforeIndex, 0, spec)
    setColumns(columns)
  }

  const moveColumn = (from: number, to: number) => {
    if (from === to || from < 0 || from >= builder.columns.length) return
    const columns = builder.columns.slice()
    const [spec] = columns.splice(from, 1)
    columns.splice(to > from ? to - 1 : to, 0, spec)
    setColumns(columns)
  }

  const addFilter = (name: string) => {
    const field = fieldMeta(datasets, builder.dataset, name)
    if (!field || !field.filterable || builder.filters.includes(name)) return
    const values = { ...builder.values }
    if (field.type === 'date' && !(name + '_from' in values)) {
      values[name + '_from'] = isoDate(30)
      values[name + '_to'] = isoDate(0)
    }
    setBuilder({ ...builder, filters: [...builder.filters, name], values })
  }

  /* Drag & drop nativ: din paleta pleaca numele campului, dintr-o coloana
     deja pusa pleaca pozitia ei - asa se poate si reordona. */
  const onDrop = (zone: 'columns' | 'filters') => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setOver(null)
    const payload = e.dataTransfer.getData('text/plain') || ''
    if (!payload || !builder.dataset) return
    const isColumn = payload.startsWith('column:')
    const value = payload.replace(/^(field|column):/, '')
    const target = (e.target as HTMLElement).closest('.bb-column') as HTMLElement | null
    const targetIndex = target ? Number(target.dataset.index) : -1

    if (isColumn) {
      /* O coloana nu se muta in zona de filtre: filtrul se ia din paleta. */
      if (zone === 'columns') moveColumn(Number(value), targetIndex === -1 ? builder.columns.length : targetIndex)
      return
    }
    if (!fieldMeta(datasets, builder.dataset, value)) return
    if (zone === 'filters') addFilter(value)
    else addColumn(value, targetIndex)
  }

  const builderFilterValues = (): BiFilterValues => {
    const out: BiFilterValues = {}
    for (const [name, value] of Object.entries(builder.values)) {
      const base = name.replace(/_(from|to)$/, '')
      if (base !== name && typeof builder.values[base + '_period'] === 'string' && builder.values[base + '_period'] !== '') continue
      if (Array.isArray(value) ? value.length > 0 : value !== '') out[name] = value
    }
    return out
  }

  const doPreview = () => {
    setBusy('preview')
    previewBiReport(builder.dataset, builderDefinition(builder), builderFilterValues())
      .then((result) => setPreview(result))
      .catch((error: unknown) => {
        setPreview(null)
        toast(failMessage(error, 'The preview could not be generated'), 'Error', 'error')
      })
      .finally(() => setBusy(null))
  }

  const doSave = () => {
    /* Numele lipseste: se arata CASUTA, nu doar vestea. */
    if (builder.name.trim() === '') {
      setNeedsName(true)
      nameBox.current?.focus()
      toast('Give the report a name — the box next to Save', 'Error', 'error')
      return
    }
    setBusy('save')
    saveBiCustomReport(builder.editing, builder.name, builder.dataset, builderDefinition(builder), builder.roles)
      .then((saved) => {
        onSaved(saved.key, saved.entry)
        reset('')
        toast('The report was saved and added to the list above', 'Saved', 'success')
        if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0, behavior: 'smooth' })
      })
      .catch((error: unknown) => toast(failMessage(error, 'The report could not be saved'), 'Error', 'error'))
      .finally(() => setBusy(null))
  }

  return (
    <>
      <h4>
        <strong>Report builder</strong> — define any report, without code
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Pick a data source, then drag fields into Columns and Filters. A column can show the value or a figure over it — count,
        total, average, smallest, largest — and a date column can be cut by day, week, month, quarter or year. Preview the
        result, give it a name and save: the report joins the list above, with its filters and its XLSX and PDF exports.
      </label>

      <div className="row" style={{ marginTop: 15 }}>
        <div className="col-md-4">
          <div className="form-group">
            <label className="control-label" htmlFor="bb-dataset">
              Data source:
            </label>
            <select id="bb-dataset" className="form-control" value={builder.dataset} onChange={(e) => reset(e.target.value)}>
              <option value="">Select a data source</option>
              {Object.entries(datasets).map(([key, ds]) => (
                <option key={key} value={key}>
                  {withDataMark(ds.label, ds)}
                </option>
              ))}
            </select>
          </div>
          <p id="bb-dataset-note" className="text-muted" style={{ fontSize: 12 }}>
            {dataset
              ? (dataset.has_data === false
                  ? 'Nothing has been written to this source on this instance yet — a report over it comes out empty until the module that feeds it starts reporting. '
                  : '') + (dataset.note || '')
              : ''}
          </p>

          <label className="control-label">Available fields:</label>
          <p className="bb-hint">Drag a field to the right — or click it to add it as a column.</p>
          <div id="bb-palette" className="bb-palette">
            {!dataset ? (
              <span className="bb-hint">Pick a data source first.</span>
            ) : (
              dataset.fields.map((field) => (
                <span
                  key={field.name}
                  className="bb-chip bb-field"
                  draggable
                  data-name={field.name}
                  title={field.filterable ? 'Drag into Columns or Filters' : 'Drag into Columns'}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', 'field:' + field.name)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onClick={() => addColumn(field.name, null)}
                >
                  <i className={'fas ' + typeIcon(field.type)} aria-hidden="true"></i> {field.label}
                </span>
              ))
            )}
          </div>
        </div>

        <div className="col-md-8">
          <label className="control-label">
            Columns <small className="text-muted">(drag fields here; drag to reorder; the box on a column turns it into a figure)</small>:
          </label>
          <div
            id="bb-columns"
            className={'bb-dropzone' + (over === 'columns' ? ' bb-over' : '')}
            data-zone="columns"
            onDragOver={(e) => {
              e.preventDefault()
              setOver('columns')
            }}
            onDragLeave={() => setOver(null)}
            onDrop={onDrop('columns')}
          >
            {builder.columns.length === 0 ? <span className="bb-hint">Drop fields here — they become the columns of the report.</span> : null}
            {builder.columns.map((spec, index) => {
              const field = fieldMeta(datasets, builder.dataset, spec.field)
              if (!field) return null
              return (
                <span
                  key={index}
                  className={'bb-chip bb-column' + (spec.agg ? ' bb-computed' : '')}
                  draggable
                  data-index={index}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', 'column:' + index)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                >
                  <i className={'fas ' + typeIcon(field.type)} aria-hidden="true"></i> {field.label}
                  {field.aggregates.length > 0 ? (
                    <select
                      className="form-control bb-agg"
                      draggable={false}
                      title="What this column shows"
                      aria-label={field.label + ' figure'}
                      value={spec.agg}
                      onChange={(e) => {
                        const columns = builder.columns.slice()
                        /* O socoteala inghite felia de timp: MIN(luna) n-ar mai fi o luna. */
                        columns[index] = { ...spec, agg: e.target.value, bucket: e.target.value ? '' : spec.bucket }
                        setColumns(columns)
                      }}
                    >
                      <option value="">the value</option>
                      {field.aggregates.map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {field.type === 'date' && !spec.agg && field.buckets.length > 0 ? (
                    <select
                      className="form-control bb-bucket"
                      draggable={false}
                      title="Cut the date into periods"
                      aria-label={field.label + ' period cut'}
                      value={spec.bucket}
                      onChange={(e) => {
                        const columns = builder.columns.slice()
                        columns[index] = { ...spec, bucket: e.target.value }
                        setColumns(columns)
                      }}
                    >
                      <option value="">exact moment</option>
                      {field.buckets.map((b) => (
                        <option key={b} value={b}>
                          by {b}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <span
                    className="bb-remove"
                    title="Remove column"
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      const columns = builder.columns.slice()
                      columns.splice(index, 1)
                      setColumns(columns)
                    }}
                  >
                    &times;
                  </span>
                </span>
              )
            })}
          </div>

          <label className="control-label">
            Filters <small className="text-muted">(drag fields here; the values below are for the preview)</small>:
          </label>
          <div
            id="bb-filters"
            className={'bb-dropzone' + (over === 'filters' ? ' bb-over' : '')}
            data-zone="filters"
            onDragOver={(e) => {
              e.preventDefault()
              setOver('filters')
            }}
            onDragLeave={() => setOver(null)}
            onDrop={onDrop('filters')}
          >
            {builder.filters.length === 0 ? <span className="bb-hint">Drop fields here — whoever runs the report will filter by them.</span> : null}
            {builder.filters.map((name) => {
              const field = fieldMeta(datasets, builder.dataset, name)
              if (!field) return null
              const required = field.required
              return (
                <span
                  key={name}
                  className={'bb-filter-chip' + (required ? ' bb-chip bb-required-chip' : '')}
                  data-name={name}
                  title={required ? 'Required period — it always filters this report' : undefined}
                >
                  <i className={'fas ' + typeIcon(field.type)} aria-hidden="true"></i> {field.label}
                  {required ? <small> (required)</small> : null}
                  <FilterChipInputs
                    field={field}
                    values={builder.values}
                    periods={page.periods}
                    operators={page.operators}
                    onChange={(key, value) => setBuilder({ ...builder, values: { ...builder.values, [key]: value } })}
                  />
                  {required ? null : (
                    <span
                      className="bb-remove"
                      title="Remove filter"
                      role="button"
                      onClick={() => setBuilder({ ...builder, filters: builder.filters.filter((f) => f !== name) })}
                    >
                      &times;
                    </span>
                  )}
                </span>
              )
            })}
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label className="control-label" htmlFor="bb-sort">
                  Sort by:
                </label>
                <div>
                  <select id="bb-sort" className="form-control" style={{ display: 'inline-block', width: '65%' }} value={builder.sortBy} onChange={(e) => setBuilder({ ...builder, sortBy: e.target.value })}>
                    {builder.columns.map((spec) => {
                      const field = fieldMeta(datasets, builder.dataset, spec.field)
                      return field ? (
                        <option key={columnKey(spec)} value={columnKey(spec)}>
                          {columnLabel(field, spec)}
                        </option>
                      ) : null
                    })}
                  </select>
                  <select id="bb-dir" className="form-control" aria-label="Sort direction" style={{ display: 'inline-block', width: '32%' }} value={builder.sortDir} onChange={(e) => setBuilder({ ...builder, sortDir: e.target.value === 'desc' ? 'desc' : 'asc' })}>
                    <option value="asc">A → Z</option>
                    <option value="desc">Z → A</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="control-label">&nbsp;</label>
                <div className="checkbox" style={{ margin: 0 }}>
                  <label>
                    <input id="bb-group" type="checkbox" checked={builder.group} onChange={(e) => setBuilder({ ...builder, group: e.target.checked })} /> Group identical rows, add a Count column
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label className="control-label" htmlFor="bb-roles">
                  Visible to roles:
                </label>
                <SelectPicker
                  id="bb-roles"
                  className="form-control"
                  title="every colleague"
                  multiple
                  value={builder.roles}
                  options={page.roles.map((r) => ({ value: String(r.id), label: r.name }))}
                  onChange={(roles) => setBuilder({ ...builder, roles })}
                />
                <p className="bb-hint">
                  Click to pick the roles allowed to see and run this report. Nothing picked means every colleague sees it. You
                  always see your own reports.
                </p>
              </div>
            </div>
          </div>

          <div className="bb-actions">
            <input
              id="bb-name"
              ref={nameBox}
              type="text"
              className={'form-control' + (needsName ? ' bb-needs-name' : '')}
              maxLength={150}
              placeholder="Name this report, then save it"
              aria-label="Report name"
              value={builder.name}
              onChange={(e) => {
                setNeedsName(false)
                setBuilder({ ...builder, name: e.target.value })
              }}
            />
            <button id="bb-preview" type="button" className="btn btn-primary" disabled={!ready || busy !== null} onClick={doPreview}>
              <i className="fas fa-eye" aria-hidden="true"></i> Preview
            </button>
            <button id="bb-save" type="button" className="btn btn-success" disabled={!ready || busy !== null} onClick={doSave}>
              <i className="fas fa-save" aria-hidden="true"></i> Save report
            </button>
            {builder.editing > 0 ? (
              <button id="bb-cancel-edit" type="button" className="btn btn-default" onClick={() => reset('')}>
                Cancel editing
              </button>
            ) : null}
            <span id="bb-editing" className="text-muted" style={{ marginLeft: 10 }}>
              {builder.editing > 0 ? 'Editing: ' + builder.editingName : ''}
            </span>
          </div>

          <div id="bb-result" style={{ overflowX: 'auto' }}>
            {busy === 'preview' ? <Spinner /> : null}
            <EmptyNote result={preview} />
            {preview ? <PagedTable id="bb-preview-table" columns={preview.columns} rows={preview.rows} /> : null}
          </div>
        </div>
      </div>
    </>
  )
}

/* -------------------------------------------------------------------------
 * Pagina
 * ---------------------------------------------------------------------- */

export function BiReportsPage() {
  return (
    <CompanyReportsFrame current="bi">
      <BiReportsPanel />
    </CompanyReportsFrame>
  )
}

function BiReportsPanel() {
  const page = useQuery(biReportsPageQuery)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error) return <div className="alert alert-danger">{failMessage(page.error, 'The reports could not be loaded.')}</div>
  if (!page.data) return <Spinner />

  return <BiReportsScreen page={page.data} />
}

function BiReportsScreen({ page }: { page: BiPage }) {
  /* Registrul, ca in scriptul vechi (BI_CATALOG): un raport salvat intra in
     lista fara reincarcare, unul sters iese din ea. */
  const [catalog, setCatalog] = useState<Record<string, BiCatalogEntry>>(page.catalog)
  const [selected, setSelected] = useState('')
  const [values, setValues] = useState<BiFilterValues>({})
  const [result, setResult] = useState<BiResult | null>(null)
  const [busy, setBusy] = useState<'run' | 'xlsx' | 'pdf' | 'delete' | null>(null)
  const [builder, setBuilder] = useState<Builder>(() => emptyBuilder('', page.builder))

  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [answer, setAnswer] = useState<BiAnswer | null>(null)

  const entry = selected ? catalog[selected] : undefined
  const custom = entry?.custom

  const pick = (key: string) => {
    setSelected(key)
    setResult(null)
    setValues(key && catalog[key] ? initialFilterValues(catalog[key].filters) : {})
  }

  const groups = useMemo(() => {
    const out: Record<string, { key: string; label: string; def: BiCatalogEntry }[]> = {}
    for (const [key, def] of Object.entries(catalog)) {
      ;(out[def.group] ??= []).push({ key, label: def.label, def })
    }
    return out
  }, [catalog])

  const apply = () => {
    if (!selected) return
    setBusy('run')
    runBiReport(selected, collectFilters(values))
      .then((r) => setResult(r))
      .catch((error: unknown) => {
        setResult(null)
        toast(failMessage(error, 'The report could not be generated'), 'Error', 'error')
      })
      .finally(() => setBusy(null))
  }

  const exportFile = (kind: 'xlsx' | 'pdf') => {
    if (!selected || !entry) return
    setBusy(kind)
    const filters = collectFilters(values)
    const job = kind === 'xlsx' ? downloadBiXlsx(selected, filters) : downloadBiPdf(selected, filters, filterSummary(entry.filters, values))
    job
      .catch((error: unknown) => toast(failMessage(error, 'The export could not be generated'), 'Error', 'error'))
      .finally(() => setBusy(null))
  }

  const openInBuilder = (dataset: string, definition: BiAnswer['definition'], extra: Partial<Builder>) => {
    if (!page.builder[dataset]) return
    const base = emptyBuilder(dataset, page.builder)
    const columns = definition.columns.map((c) => ({ field: c.field, agg: c.agg || '', bucket: c.bucket || '' }))
    const keys = columns.map(columnKey)
    setBuilder({
      ...base,
      columns,
      filters: definition.filters.slice(),
      values: { ...base.values },
      sortBy: keys.includes(definition.sort.by) ? definition.sort.by : (keys[0] ?? ''),
      sortDir: definition.sort.dir === 'desc' ? 'desc' : 'asc',
      group: !!definition.group,
      ...extra,
    })
    window.setTimeout(() => {
      const box = document.getElementById('bb-dataset')
      if (box && typeof box.scrollIntoView === 'function') box.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }

  const editCustom = () => {
    if (!custom) return
    openInBuilder(custom.dataset, custom.definition, {
      name: custom.name,
      roles: (custom.roles ?? []).map(String),
      editing: custom.id,
      editingName: custom.name,
    })
  }

  const deleteCustom = () => {
    if (!custom || !window.confirm('Delete the report "' + custom.name + '"? This cannot be undone.')) return
    setBusy('delete')
    deleteBiCustomReport(custom.id)
      .then(() => {
        const next = { ...catalog }
        delete next[selected]
        setCatalog(next)
        pick('')
        toast('The report was deleted', 'Deleted', 'success')
      })
      .catch((error: unknown) => toast(failMessage(error, 'The report could not be deleted'), 'Error', 'error'))
      .finally(() => setBusy(null))
  }

  const ask = (text: string) => {
    const q = text.trim()
    if (!q) return
    setAsking(true)
    askBiReport(q)
      .then((a) => setAnswer(a))
      .catch((error: unknown) => {
        /* Zona se strange la loc; raportul dinainte ramane tinut minte. */
        toast(failMessage(error, 'The question could not be answered'), 'Error', 'error')
      })
      .finally(() => setAsking(false))
  }

  const onAskKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      ask(question)
    }
  }

  const rowInfo = (r: BiResult) => r.rows.length + ' rows' + (r.rows.length >= r.limit ? ' (first ' + r.limit + ')' : '')

  return (
    <div>
      <h4>
        <strong>BI Reports</strong> — ITIL processes
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Predefined reports over incidents, problems, changes, CMDB and inventory, configurable through filters — plus your own
        reports, defined in the no-code builder below
      </label>

      {Object.keys(page.coverage).length > 0 ? (
        <div className="bi-itil">
          <span className="bi-itil-label">Processes covered</span>
          {Object.entries(page.coverage).map(([process, count]) => (
            <span key={process} className={'bi-itil-chip' + (count === 0 ? ' is-off' : '')} title={count + ' predefined report' + (count === 1 ? '' : 's')}>
              {process}
              <b>{count}</b>
            </span>
          ))}
          <span className="bi-itil-note">plus anything you build below — a report saved in the builder joins the list above</span>
        </div>
      ) : null}

      <div className="bi-ask">
        <label className="control-label" htmlFor="bi-ask-input" style={{ marginBottom: 2 }}>
          <i className="fas fa-comment-dots" aria-hidden="true"></i> Ask for a report in your own words
        </label>
        <div className="bi-ask-row">
          <input
            id="bi-ask-input"
            type="text"
            className="form-control"
            maxLength={500}
            placeholder="e.g. which windows endpoints reported in the last 7 days"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={onAskKey}
          />
          <button id="bi-ask-go" type="button" className="btn btn-primary" disabled={asking} onClick={() => ask(question)}>
            <i className="fas fa-paper-plane" aria-hidden="true"></i> Ask
          </button>
        </div>
        <p className="bb-hint" style={{ marginTop: 4 }}>
          Try:{' '}
          {ASK_EXAMPLES.map((example, i) => (
            <span key={example}>
              {i > 0 ? ' · ' : ''}
              <a
                href="#"
                className="bi-ask-example"
                onClick={(e) => {
                  e.preventDefault()
                  setQuestion(example)
                  ask(example)
                }}
              >
                {example}
              </a>
            </span>
          ))}
          <br />
          Questions are read here, on the instance — nothing leaves it. Name what you are looking for and the period. It knows
          tickets and their history, devices, installed software and what changed in it, updates and missing patches, disk
          space, running processes, services, agent errors, sign-ins, syslog, network discovery, the CMDB, monitoring, printing,
          users, companies, contracts, the service catalog, the audit trail and the CVE catalog. The answer is a real report —
          open it in the builder below to adjust it, name it and save it.
        </p>

        {asking || answer ? (
          <div id="bi-ask-answer">
            {asking ? <Spinner /> : null}
            {answer && !asking ? (
              <>
                <p id="bi-ask-note" className="text-muted" style={{ margin: '6px 0 4px' }}>
                  {answer.note}
                </p>
                <div style={{ marginBottom: 8 }}>
                  <button
                    id="bi-ask-builder"
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => openInBuilder(answer.dataset, answer.definition, { values: { ...emptyBuilder(answer.dataset, page.builder).values, ...answer.values }, name: answer.title })}
                  >
                    <i className="fas fa-sliders-h" aria-hidden="true"></i> Open in builder
                  </button>
                  <span id="bi-ask-rows" className="text-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                    {rowInfo(answer)}
                  </span>
                </div>
                <div id="bi-ask-result" style={{ overflowX: 'auto' }}>
                  <EmptyNote result={answer} />
                  <PagedTable id="bi-ask-table" columns={answer.columns} rows={answer.rows} />
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <hr />

      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label className="control-label" htmlFor="bi-report-select">
              Report:
            </label>
            <select id="bi-report-select" className="form-control" value={selected} onChange={(e) => pick(e.target.value)}>
              <option value="">Select a report</option>
              {Object.entries(groups).map(([group, reports]) => (
                <optgroup key={group} label={group}>
                  {reports.map((r) => (
                    <option key={r.key} value={r.key}>
                      {withDataMark(r.label, r.def)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <p id="bi-note" className="text-muted" style={{ fontSize: 12 }}>
            {entry?.note ?? ''}
          </p>
        </div>
        <div className="col-md-8">{entry ? <ReportFilters filters={entry.filters} values={values} onChange={setValues} /> : <div id="bi-filters" className="row"></div>}</div>
      </div>

      <div className="row">
        <div className="col-md-12" style={{ marginBottom: 15 }}>
          <button id="bi-apply" type="button" className="btn btn-primary" disabled={!selected || busy !== null} onClick={apply}>
            <i className="fas fa-play" aria-hidden="true"></i> Apply
          </button>{' '}
          <button id="bi-export" type="button" className="btn btn-success" disabled={!selected || busy !== null} onClick={() => exportFile('xlsx')}>
            <i className="fas fa-file-excel" aria-hidden="true"></i> Export XLSX
          </button>{' '}
          <button id="bi-export-pdf" type="button" className="btn btn-success" disabled={!selected || busy !== null} onClick={() => exportFile('pdf')}>
            <i className="fas fa-file-pdf" aria-hidden="true"></i> Export PDF
          </button>{' '}
          {custom ? (
            <>
              <button id="bi-edit-custom" type="button" className="btn btn-default" onClick={editCustom}>
                <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit in builder
              </button>{' '}
              <button id="bi-delete-custom" type="button" className="btn btn-danger" disabled={busy !== null} onClick={deleteCustom}>
                <i className="fas fa-trash" aria-hidden="true"></i> Delete report
              </button>
            </>
          ) : null}
          <span id="bi-row-info" className="text-muted" style={{ marginLeft: 10 }}>
            {result ? rowInfo(result) : ''}
          </span>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div id="bi-result" style={{ overflowX: 'auto' }}>
            {busy === 'run' ? <Spinner /> : null}
            <EmptyNote result={result} />
            {result ? <PagedTable id="bi-report-table" columns={result.columns} rows={result.rows} /> : null}
          </div>
        </div>
      </div>

      <hr />

      <ReportBuilder
        page={page}
        builder={builder}
        setBuilder={setBuilder}
        onSaved={(key, saved) => {
          setCatalog({ ...catalog, [key]: saved })
          setSelected(key)
          setResult(null)
          setValues(initialFilterValues(saved.filters))
        }}
      />

      <div id="bi-bottom-space" aria-hidden="true"></div>
    </div>
  )
}
